import { useState } from "react";
import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { SupportTrip } from "domain/entities/support-trip";
import { SupportTripStatusEnum } from "domain/enum/supportTripStatus";
import { MessageTypeEnum } from "domain/enum/messageType";
import { useSupportTripById, useUpdateSupportTripMutation } from "presentation/hooks/use-query-support-trip";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { formatDateToPT } from "presentation/utils/formatDateToPT";
import { formatCurrency } from "presentation/mobile/utils/format.util";
import { getApiErrorMessage } from "presentation/mobile/utils/api-error.util";

// Time for the user to see the result before the sheet closes (same as MobileExpenseApprovalForm)
const CLOSE_DELAY_MS = 1500;

type Decision = "approve" | "reject";

interface TripApprovalFormProps {
    supportTrip: SupportTrip;
    onClose: () => void;
    showMessage: (type: MessageTypeEnum, subject: string, message: string) => void;
}

// Mobile version of the web ApprovalForm: closes only after the request finishes, so the toast is shown (#938)
const TripApprovalForm = ({ supportTrip, onClose, showMessage }: TripApprovalFormProps) => {
    const { email: userEmployeeEmail } = useAuthStore();
    const { data: supportTripData } = useSupportTripById(supportTrip.id);
    const { mutateAsync: updateSupportTrip } = useUpdateSupportTripMutation();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [confirmAction, setConfirmAction] = useState<Decision | null>(null);
    const [visibleRejectionReason, setVisibleRejectionReason] = useState(false);
    const [reasonReject, setReasonReject] = useState("");

    const currentTrip = supportTripData?.data ?? supportTrip;
    const isCancellationRequest = currentTrip.status === SupportTripStatusEnum.CANCELAMENTO_PENDENTE;
    const canDecide = currentTrip.status === SupportTripStatusEnum.A_APROVAR || isCancellationRequest;

    const getNextStatus = (decision: Decision): string => {
        if (decision === "approve") {
            return isCancellationRequest ? SupportTripStatusEnum.CANCELADO : SupportTripStatusEnum.APROVADO;
        }
        // Rejecting a cancellation keeps the trip approved, with the reason (shown as "Aprovado *")
        return isCancellationRequest ? SupportTripStatusEnum.APROVADO : SupportTripStatusEnum.NAO_APROVADO;
    };

    const getSuccessMessage = (decision: Decision): string => {
        if (isCancellationRequest) {
            return decision === "approve"
                ? "Cancelamento da viagem aprovado com sucesso!"
                : "Cancelamento da viagem rejeitado com sucesso!";
        }
        return decision === "approve" ? "Viagem aprovada com sucesso!" : "Viagem rejeitada com sucesso!";
    };

    const submitDecision = async (decision: Decision) => {
        setConfirmAction(null);
        if (!userEmployeeEmail || !canDecide || isSubmitting) return;

        setIsSubmitting(true);
        setVisibleRejectionReason(false);

        try {
            await updateSupportTrip({
                ...currentTrip,
                status: getNextStatus(decision),
                rejectionReason: decision === "reject" ? reasonReject : currentTrip.rejectionReason,
                lastModifiedBy: userEmployeeEmail,
            });
            showMessage(MessageTypeEnum.SUCCESS, "Sucesso", getSuccessMessage(decision));
            setTimeout(onClose, CLOSE_DELAY_MS);
        } catch (error) {
            showMessage(
                MessageTypeEnum.ERROR,
                "Erro",
                getApiErrorMessage(error, "Erro ao realizar a ação na viagem.")
            );
            setIsSubmitting(false);
        }
    };

    const details = [
        { label: "Colaborador", value: currentTrip.employee?.name },
        { label: "Data", value: formatDateToPT(currentTrip.dateTrip) },
        { label: "Origem", value: currentTrip.locationOrigin },
        { label: "Destino", value: currentTrip.locationDestination },
        { label: "Distância", value: `${currentTrip.distanceKm} km` },
        { label: "Valor", value: formatCurrency(currentTrip.tripValue) },
        { label: "Estado", value: currentTrip.status },
    ];

    return (
        <div className="flex flex-column gap-3">
            {/* Detalhes da viagem */}
            <div className="flex flex-column gap-2">
                {details.map((detail) => (
                    <div key={detail.label} className="flex justify-content-between gap-3 text-sm">
                        <span style={{ color: "#64748B" }}>{detail.label}</span>
                        <span className="font-semibold text-right" style={{ color: "#0F172A" }}>
                            {detail.value || "-"}
                        </span>
                    </div>
                ))}
            </div>

            {/* Ações */}
            {canDecide && (
                <div className="flex justify-content-end gap-2 pt-3 border-top-1 surface-border">
                    <Button
                        label="Rejeitar"
                        icon="pi pi-times"
                        severity="danger"
                        outlined
                        size="small"
                        disabled={isSubmitting}
                        onClick={() => {
                            setReasonReject("");
                            setVisibleRejectionReason(true);
                        }}
                    />
                    <Button
                        label={isSubmitting ? "A guardar..." : "Aprovar"}
                        icon={isSubmitting ? "pi pi-spin pi-spinner" : "pi pi-check"}
                        size="small"
                        disabled={isSubmitting}
                        style={{ backgroundColor: "#059669", borderColor: "#059669" }}
                        onClick={() => setConfirmAction("approve")}
                    />
                </div>
            )}

            <ConfirmDialog
                visible={confirmAction !== null}
                onHide={() => setConfirmAction(null)}
                message={
                    confirmAction === "approve"
                        ? isCancellationRequest
                            ? "Deseja aprovar o cancelamento desta viagem?"
                            : "Deseja aprovar esta viagem?"
                        : isCancellationRequest
                            ? "Deseja rejeitar o cancelamento desta viagem, sendo este o motivo?"
                            : "Deseja rejeitar esta viagem, sendo este o motivo?"
                }
                header="Confirmação"
                icon="pi pi-exclamation-triangle"
                acceptLabel="Sim"
                rejectLabel="Não"
                accept={() => confirmAction && submitDecision(confirmAction)}
            />

            {/* Motivo da rejeição */}
            <Dialog
                header={isCancellationRequest ? "Rejeição do Cancelamento da Viagem" : "Rejeição da Viagem"}
                visible={visibleRejectionReason}
                modal
                onHide={() => !isSubmitting && setVisibleRejectionReason(false)}
                className="mx-3"
                style={{ width: "92vw", maxWidth: "420px" }}
            >
                <label htmlFor="motivo-viagem" className="text-xs font-semibold text-700">
                    Informe o Motivo da Rejeição:
                </label>
                <InputTextarea
                    id="motivo-viagem"
                    value={reasonReject}
                    onChange={(e) => setReasonReject(e.target.value)}
                    rows={4}
                    autoFocus
                    maxLength={190}
                    className="w-full mt-1"
                />
                <div className="flex justify-content-end mt-1">
                    <small className="text-500">{reasonReject.length}/190 caracteres</small>
                </div>
                <div className="flex justify-content-end gap-2 mt-3">
                    <Button
                        label="Cancelar"
                        severity="secondary"
                        outlined
                        size="small"
                        disabled={isSubmitting}
                        onClick={() => setVisibleRejectionReason(false)}
                    />
                    <Button
                        label="Rejeitar"
                        icon="pi pi-check"
                        severity="danger"
                        size="small"
                        disabled={isSubmitting}
                        onClick={() => {
                            if (reasonReject.trim() === "") {
                                showMessage(MessageTypeEnum.ERROR, "Erro", "Informe o motivo da rejeição.");
                                return;
                            }
                            setConfirmAction("reject");
                        }}
                    />
                </div>
            </Dialog>
        </div>
    );
};

export default TripApprovalForm;
